import { ReactNode } from 'react'
import { Navigate, useParams } from 'react-router-dom'
import { useMe } from '@/hooks/useMe'

type Scope = 'admin' | 'provider' | 'clinic' | 'clinic_group' | 'ad_supplier' | 'ad_supplier_group'

const paramKey: Record<Exclude<Scope, 'admin'>, string> = {
  provider: 'provider_id',
  clinic: 'clinic_id',
  clinic_group: 'clinic_group_id',
  ad_supplier: 'supplier_id',
  ad_supplier_group: 'ad_supplier_group_id',
}

interface Props {
  scope: Scope
  children: ReactNode
}

export function ScopedRoute({ scope, children }: Props) {
  const { user, isLoading } = useMe()
  const params = useParams()

  if (isLoading) return null // auth hydrate 前不判斷
  if (!user) return <Navigate to="/login" replace />

  // 'admin' scope: only sys_admin allowed; no id param to validate
  if (scope === 'admin') {
    if (user.role_type !== 'sys_admin') return <Navigate to="/" replace />
    return <>{children}</>
  }

  const idStr = params[paramKey[scope]]
  const id = Number(idStr)
  if (!Number.isFinite(id) || id <= 0) return <Navigate to="/" replace />

  // sys_admin 全過
  if (user.role_type === 'sys_admin') return <>{children}</>

  // 檢查 user 是否有該 scope 的權限
  const allowed = checkScopeAccess(user, scope, id)
  if (!allowed) return <Navigate to="/" replace />

  return <>{children}</>
}

function checkScopeAccess(
  user: {
    role_type?: string
    scopes?: {
      provider_id: number | null
      clinic_ids: number[]
      supplier_ids: number[]
      clinic_group_ids: number[]
      ad_supplier_group_ids: number[]
    }
  },
  scope: Exclude<Scope, 'admin'>,
  id: number,
): boolean {
  const s = user.scopes
  if (!s) return false

  // Hierarchical access: provider-scoped users can enter any of their
  // child resources (clinic / clinic_group / ad_supplier / ad_supplier_group).
  // Backend API enforces fine-grained permission (e.g. clinic belongs to
  // this provider); the frontend guard only screens the obvious cases.
  if (s.provider_id && scope !== 'provider') return true

  switch (scope) {
    case 'provider':
      return s.provider_id === id
    case 'clinic':
      // Direct clinic user, or clinic_group user (API verifies group membership)
      return s.clinic_ids.includes(id) || (s.clinic_group_ids?.length ?? 0) > 0
    case 'clinic_group':
      return s.clinic_group_ids.includes(id)
    case 'ad_supplier':
      return s.supplier_ids.includes(id) || (s.ad_supplier_group_ids?.length ?? 0) > 0
    case 'ad_supplier_group':
      return s.ad_supplier_group_ids.includes(id)
  }
}
